/**
 * WBO curiosity-card typography engine.
 *
 * Deterministic, measurement-driven layout for the 1080×1080 WBO graphic:
 * a photo on top and a dark text panel below with a headline and a short
 * description, exactly one coloured significant word per rendered line and
 * left-aligned text (never justified).
 *
 * The photo never bleeds to the card edges: it is inset by the same tokens as
 * the panel text (see `wboPhotoBox`), so the card background frames it
 * exactly like it frames the headline and description.
 *
 * The engine has no DOM dependency: callers inject a text measurement function
 * so the same algorithm can be unit-tested and mirrored in different renderers.
 */

export type WboFormat = 'STANDARD' | 'EXTENDED';
export type WboFormatChoice = WboFormat | 'AUTO';
export type WboSection = 'headline' | 'description';
export type WboPaletteColor = { name: string; hex: string; enabled: boolean };
export type WboLineHighlight = { section: WboSection; line: number; lineText: string; word: string; color: string };
export type WboGraphicConfig = { backgroundColor: string; textColor: string; borderColor: string; borderWidth: number; highlightMode: 'RANDOM' | 'SEQUENTIAL'; palette: WboPaletteColor[] };

/** Measures the rendered width of `text` in the given CSS font shorthand. */
export type WboMeasure = (text: string, font: string) => number;

export interface WboBlockTokens {
  family: string;
  weight: number;
  /** Preferred starting size; the engine stays within [min, max]. */
  start: number;
  min: number;
  max: number;
  preferredLines: number;
  maxLines: number;
  lineHeight: number;
  uppercase: boolean;
}

export interface WboTypographyTokens {
  headline: WboBlockTokens;
  description: WboBlockTokens;
  /** Vertical gap between the headline and the description, in px. */
  gap: number;
  /** Left/right panel padding, in px. */
  sideMargin: number;
  /** Top/bottom panel padding, in px. */
  verticalPadding: number;
  /** Outer border width, in px. */
  border: number;
}

export const WBO_FONT_STACKS = {
  headline: '"Barlow Condensed", "Barlow Condensed ExtraBold", Arial, sans-serif',
  description: '"Inter", "Inter SemiBold", Arial, sans-serif',
} as const;

export const WBO_TYPOGRAPHY: WboTypographyTokens = {
  headline: { family: WBO_FONT_STACKS.headline, weight: 800, start: 58, min: 54, max: 62, preferredLines: 2, maxLines: 4, lineHeight: 1.06, uppercase: true },
  description: { family: WBO_FONT_STACKS.description, weight: 600, start: 36, min: 33, max: 38, preferredLines: 3, maxLines: 5, lineHeight: 1.16, uppercase: false },
  gap: 16,
  sideMargin: 40,
  // Same 40 px as the side margin so the card background frames the photo with one
  // uniform margin all around (owner decision, 2026-10-17); the text panel uses the
  // same token for its own top/bottom padding.
  verticalPadding: 40,
  border: 4,
};

/** Photo share of the 1080px height per format. */
export const WBO_FORMAT_PHOTO_RATIO: Record<WboFormat, number> = { STANDARD: 0.67, EXTENDED: 0.60 };

/** Drawing box of the illustration inside the 1080×1080 card. */
export interface WboPhotoBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Inset illustration box for a photo zone of `photoHeight` px: the same
 * left/right margin as the panel text and the same top padding as the panel,
 * so the card background frames the photo instead of the photo bleeding to
 * the card edges. The bottom edge stays on the photo/panel boundary.
 */
export function wboPhotoBox(photoHeight: number, tokens: WboTypographyTokens): WboPhotoBox {
  return {
    x: tokens.sideMargin,
    y: tokens.verticalPadding,
    width: Math.max(0, 1080 - tokens.sideMargin * 2),
    height: Math.max(0, photoHeight - tokens.verticalPadding),
  };
}

export interface WboTokenOverrides {
  headline?: Partial<WboBlockTokens>;
  description?: Partial<WboBlockTokens>;
  gap?: number;
  sideMargin?: number;
  border?: number;
}

/** Merge persisted per-project typography overrides onto the engine defaults. */
export function mergeWboTokens(base: WboTypographyTokens, overrides?: WboTokenOverrides | null): WboTypographyTokens {
  const o = overrides ?? {};
  return {
    headline: { ...base.headline, ...(o.headline ?? {}) },
    description: { ...base.description, ...(o.description ?? {}) },
    gap: typeof o.gap === 'number' ? o.gap : base.gap,
    sideMargin: typeof o.sideMargin === 'number' ? o.sideMargin : base.sideMargin,
    verticalPadding: base.verticalPadding,
    border: typeof o.border === 'number' ? o.border : base.border,
  };
}

export interface WboRenderedLine {
  section: WboSection;
  /** Line index within its section. */
  index: number;
  text: string;
  size: number;
  lineHeight: number;
  /** Left-aligned baseline start x. */
  x: number;
  /** Baseline y for drawing. */
  y: number;
  /** Stretch this line to both panel edges (false on the last line of a section). */
  justify: boolean;
  highlight: { word: string; color: string };
}

export interface WboTypographyPlan {
  format: WboFormat | null;
  photoHeight: number;
  panelHeight: number;
  lines: WboRenderedLine[];
  seed: number;
  needsEdit: boolean;
  error: string | null;
}

export interface WboTypographyInput {
  headline: string;
  description: string;
  measure: WboMeasure;
  candidateId: string;
  /** Format request; AUTO falls back from STANDARD to EXTENDED. */
  format?: WboFormatChoice;
  seed?: number;
  config?: Pick<WboGraphicConfig, 'highlightMode' | 'palette'>;
  tokens?: WboTypographyTokens;
  manual?: WboLineHighlight[];
}

const STOP_WORDS = new Set('a aby ach ale albo ani aż bez bo by była był były być co czy dla do gdy gdzie i ich im inna inne inny ja jak jako je jego jej jest jeszcze już że lecz lub ma mi mnie może na nad nam nas nie nich nic o od oraz po pod przez przy się są ta tak te tego tej ten to tu tych tym u w we więc z za ze'.split(' '));

function hash32(value: string): number {
  let hash = 2166136261;
  for (let i = 0; i < value.length; i += 1) { hash ^= value.charCodeAt(i); hash = Math.imul(hash, 16777619); }
  return hash >>> 0;
}

export function deriveWboSeed(parts: Array<string | number>): number {
  return hash32(parts.join('|'));
}

function significantWords(line: string): string[] {
  const tokens = line.match(/[\p{L}\p{N}]+(?:['’-][\p{L}\p{N}]+)*/gu) ?? [];
  return tokens.filter((token) => token.length > 1 && !STOP_WORDS.has(token.toLocaleLowerCase('pl-PL')));
}

function fontFor(weight: number, size: number, family: string): string {
  return `${weight} ${size}px ${family}`;
}

function splitLongWord(measure: WboMeasure, word: string, font: string, maxWidth: number): string[] {
  const pieces: string[] = [];
  let piece = '';
  for (const character of word) {
    if (piece && measure(piece + character, font) > maxWidth) { pieces.push(piece); piece = character; }
    else piece += character;
  }
  if (piece) pieces.push(piece);
  return pieces;
}

/** Greedy wrap by measured width; a single overlong word is split so it cannot overflow. */
export function wrapWboText(measure: WboMeasure, text: string, font: string, maxWidth: number): string[] {
  const lines: string[] = [];
  let current = '';
  const append = (word: string): void => {
    if (!current) {
      if (measure(word, font) <= maxWidth) { current = word; return; }
      const pieces = splitLongWord(measure, word, font, maxWidth);
      lines.push(...pieces.slice(0, -1));
      current = pieces[pieces.length - 1] ?? '';
      return;
    }
    if (measure(`${current} ${word}`, font) <= maxWidth) { current = `${current} ${word}`; return; }
    lines.push(current); current = '';
    append(word);
  };
  for (const word of text.trim().split(/\s+/u).filter(Boolean)) append(word);
  if (current) lines.push(current);
  return lines;
}

/** Avoid widows/orphans and a trailing short preposition or conjunction. */
function guardBreaks(lines: string[]): string[] {
  const result = lines.map((line) => line.split(/\s+/u).filter(Boolean));
  if (result.length > 1) {
    const last = result[result.length - 1]!;
    const previous = result[result.length - 2]!;
    // A single word alone on the last line is a widow: pull a word down.
    if (last.length === 1 && previous.length > 1) last.unshift(previous.pop()!);
  }
  for (let i = 0; i < result.length - 1; i += 1) {
    const current = result[i]!;
    const tail = current[current.length - 1]?.toLocaleLowerCase('pl-PL') ?? '';
    // Do not leave a short function word dangling at the end of a line.
    if (current.length > 1 && STOP_WORDS.has(tail)) { current.pop(); result[i + 1]!.unshift(tail); }
  }
  return result.map((words) => words.join(' ')).filter((line) => line.length > 0);
}

interface FittedBlock { lines: string[]; size: number; lineHeight: number }

function fitBlock(measure: WboMeasure, text: string, tokens: WboBlockTokens, maxWidth: number, availableHeight: number): FittedBlock | null {
  const value = tokens.uppercase ? text.trim().toLocaleUpperCase('pl-PL') : text.trim();
  if (!value) return null;
  const sizes: number[] = [];
  for (let size = Math.max(tokens.max, tokens.start); size >= tokens.min; size -= 1) sizes.push(size);
  const attempt = (allowLines: number): FittedBlock | null => {
    for (const size of sizes) {
      const font = fontFor(tokens.weight, size, tokens.family);
      const wrapped = guardBreaks(wrapWboText(measure, value, font, maxWidth));
      if (wrapped.length === 0 || wrapped.length > allowLines) continue;
      if (!wrapped.every((line) => measure(line, font) <= maxWidth)) continue;
      const lineHeight = Math.round(size * tokens.lineHeight);
      if (wrapped.length * lineHeight > availableHeight) continue;
      return { lines: wrapped, size, lineHeight };
    }
    return null;
  };
  return attempt(tokens.preferredLines) ?? attempt(tokens.maxLines);
}

function chooseHighlight(line: string, section: WboSection, index: number, seed: number, palette: WboPaletteColor[], mode: 'RANDOM' | 'SEQUENTIAL', previous: string | null, manual: WboLineHighlight[]): { word: string; color: string } {
  const colors = palette.filter((color) => color.enabled).slice(0, 4);
  const words = significantWords(line);
  const lineSeed = hash32(`${seed}:${section}:${index}:${line}`);
  const manualChoice = manual.find((entry) => entry.section === section && entry.lineText === line && words.includes(entry.word));
  const word = (manualChoice && words.includes(manualChoice.word) ? manualChoice.word : undefined)
    // Prefer the longest significant word on the line for a clear highlight.
    ?? [...words].sort((a, b) => b.length - a.length || a.localeCompare(b))[lineSeed % Math.max(words.length, 1)]
    ?? line.match(/[\p{L}\p{N}]+/u)?.[0] ?? line;
  let color = manualChoice?.color && colors.some((entry) => entry.hex.toLowerCase() === manualChoice.color.toLowerCase()) ? manualChoice.color : '';
  if (!color) {
    const index0 = colors.length ? (mode === 'SEQUENTIAL' ? index % colors.length : lineSeed % colors.length) : 0;
    const chosen = colors[index0];
    if (chosen && colors.length > 1 && chosen.hex.toLowerCase() === previous) {
      color = colors[(index0 + 1) % colors.length]!.hex;
    } else color = chosen?.hex ?? '#FFD43B';
  }
  return { word, color };
}

function planFormat(input: WboTypographyInput, format: WboFormat, tokens: WboTypographyTokens, seed: number): WboTypographyPlan {
  const { measure } = input;
  const photoHeight = Math.round(1080 * WBO_FORMAT_PHOTO_RATIO[format]);
  const panelHeight = 1080 - photoHeight;
  const maxWidth = 1080 - tokens.sideMargin * 2;
  const innerHeight = panelHeight - tokens.verticalPadding * 2;
  const palette = input.config?.palette ?? [];
  if (!palette.filter((color) => color.enabled).length) {
    return { format, photoHeight, panelHeight, lines: [], seed, needsEdit: false, error: 'Paleta nie ma aktywnych kolorów.' };
  }
  // Reserve the headline first so a long headline cannot crowd the description out.
  const headline = fitBlock(measure, input.headline, tokens.headline, maxWidth, innerHeight - tokens.gap - tokens.description.min);
  if (!headline) return { format, photoHeight, panelHeight, lines: [], seed, needsEdit: true, error: 'NEEDS_EDIT: nagłówek nie mieści się w dozwolonych rozmiarach.' };
  const description = fitBlock(measure, input.description, tokens.description, maxWidth, innerHeight - tokens.gap - headline.lines.length * headline.lineHeight);
  if (!description) return { format, photoHeight, panelHeight, lines: [], seed, needsEdit: true, error: 'NEEDS_EDIT: opis nie mieści się w dozwolonych rozmiarach.' };

  const blockHeight = headline.lines.length * headline.lineHeight + tokens.gap + description.lines.length * description.lineHeight;
  if (blockHeight > innerHeight) return { format, photoHeight, panelHeight, lines: [], seed, needsEdit: true, error: 'NEEDS_EDIT: tekst nie mieści się w panelu.' };

  const lines: WboRenderedLine[] = [];
  let previous: string | null = null;
  let cursor = photoHeight + tokens.verticalPadding + Math.round((innerHeight - blockHeight) / 2);
  const push = (section: WboSection, text: string, size: number, lineHeight: number, index: number, justify: boolean) => {
    cursor += lineHeight;
    const highlight = chooseHighlight(text, section, index, seed, palette, input.config?.highlightMode ?? 'RANDOM', previous, input.manual ?? []);
    previous = highlight.color.toLowerCase();
    lines.push({ section, index, text, size, lineHeight, x: tokens.sideMargin, y: cursor - Math.round(size * 0.2), justify, highlight });
  };
  headline.lines.forEach((text, index) => push('headline', text, headline.size, headline.lineHeight, index, index < headline.lines.length - 1));
  cursor += tokens.gap;
  description.lines.forEach((text, index) => push('description', text, description.size, description.lineHeight, index, index < description.lines.length - 1));
  return { format, photoHeight, panelHeight, lines, seed, needsEdit: false, error: null };
}

/** Build a deterministic layout for the requested format, falling back STANDARD → EXTENDED for AUTO. */
export function planWboTypography(input: WboTypographyInput): WboTypographyPlan {
  const tokens = input.tokens ?? WBO_TYPOGRAPHY;
  const seed = input.seed ?? deriveWboSeed([input.candidateId, input.headline, input.description]);
  const requested: WboFormat = input.format === 'EXTENDED' ? 'EXTENDED' : 'STANDARD';
  const first = planFormat({ ...input, seed }, requested, tokens, seed);
  if (first.error && !first.needsEdit) return first; // palette error
  if ((input.format ?? 'AUTO') === 'AUTO' && (first.needsEdit || first.error)) {
    const fallback = planFormat({ ...input, seed }, requested === 'STANDARD' ? 'EXTENDED' : 'STANDARD', tokens, seed);
    if (!fallback.needsEdit && !fallback.error) return fallback;
  }
  return first;
}

export function planHighlightsFromLines(lines: WboRenderedLine[]): WboLineHighlight[] {
  return lines.map((line) => ({ section: line.section, line: line.index, lineText: line.text, word: line.highlight.word, color: line.highlight.color }));
}
